// src/features/test-cases/components/TestStepDialog.tsx
// Create/Edit test step dialog
// OpenAPI: POST /api/testcases/{testcase_id}/steps — body: { step_number (req), actions (req), expected_results (req), execution_type? (default 1) }
//          PUT /api/testcases/{testcase_id}/steps/{step_id} — body: { step_number?, actions?, expected_results?, execution_type?, active? }

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCreateTestStep, useUpdateTestStep } from '../api/useTestCases'
import type { TestStep } from '@/types/testcase.types'
import { ExecutionType } from '@/types/testcase.types'

interface TestStepDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  testCaseId: number
  testStep?: TestStep
  mode: 'create' | 'edit'
  nextStepNumber?: number
}

export function TestStepDialog({
  open,
  onOpenChange,
  testCaseId,
  testStep,
  mode,
  nextStepNumber = 1,
}: TestStepDialogProps) {
  const [stepNumber, setStepNumber] = useState(nextStepNumber.toString())
  const [actions, setActions] = useState('')
  const [expectedResults, setExpectedResults] = useState('')
  const [executionType, setExecutionType] = useState<string>(
    ExecutionType.Manual.toString()
  )
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateTestStep()
  const updateMutation = useUpdateTestStep()

  useEffect(() => {
    if (mode === 'edit' && testStep) {
      setStepNumber(testStep.step_number.toString())
      setActions(testStep.actions)
      setExpectedResults(testStep.expected_results)
      setExecutionType(testStep.execution_type.toString())
    } else {
      setStepNumber(nextStepNumber.toString())
      setActions('')
      setExpectedResults('')
      setExecutionType(ExecutionType.Manual.toString())
    }
    setError(null)
  }, [mode, testStep, nextStepNumber, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLoading) return

    setError(null)

    if (!actions.trim()) {
      setError('Actions are required')
      return
    }

    if (!expectedResults.trim()) {
      setError('Expected results are required')
      return
    }

    const stepNum = parseInt(stepNumber, 10)
    if (isNaN(stepNum) || stepNum < 1) {
      setError('Step number must be a positive number')
      return
    }

    try {
      if (mode === 'create') {
        // POST /api/testcases/{testcase_id}/steps → 201
        await createMutation.mutateAsync({
          testcaseId: testCaseId,
          stepData: {
            step_number: stepNum,
            actions: actions.trim(),
            expected_results: expectedResults.trim(),
            execution_type: parseInt(executionType, 10),
          },
        })
      } else if (testStep) {
        // PUT /api/testcases/{testcase_id}/steps/{step_id}
        await updateMutation.mutateAsync({
          testcaseId: testCaseId,
          stepId: testStep.id,
          stepData: {
            step_number: stepNum,
            actions: actions.trim(),
            expected_results: expectedResults.trim(),
            execution_type: parseInt(executionType, 10),
          },
        })
      }
      onOpenChange(false)
    } catch (err: unknown) {
      let errorMessage = 'Failed to save test step'
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } }
        if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      setError(errorMessage)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add Test Step' : 'Edit Test Step'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a new step to this test case.'
              : 'Update test step information.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="stepNumber">
                  Step Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="stepNumber"
                  type="number"
                  min="1"
                  value={stepNumber}
                  onChange={(e) => setStepNumber(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="executionType">Execution Type</Label>
                <Select
                  value={executionType}
                  onValueChange={setExecutionType}
                  disabled={isLoading}
                >
                  <SelectTrigger id="executionType">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ExecutionType.Manual.toString()}>
                      Manual
                    </SelectItem>
                    <SelectItem value={ExecutionType.Automated.toString()}>
                      Automated
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="actions">
                Actions <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="actions"
                value={actions}
                onChange={(e) => setActions(e.target.value)}
                placeholder="Describe the actions to perform in this step"
                disabled={isLoading}
                rows={4}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="expectedResults">
                Expected Results <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="expectedResults"
                value={expectedResults}
                onChange={(e) => setExpectedResults(e.target.value)}
                placeholder="Describe the expected results for this step"
                disabled={isLoading}
                rows={4}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1976D2] hover:bg-[#1565C0]"
              disabled={isLoading}
            >
              {isLoading
                ? mode === 'create'
                  ? 'Adding...'
                  : 'Updating...'
                : mode === 'create'
                  ? 'Add Step'
                  : 'Update Step'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
